import type { PriceFormatter } from '../price-formatter'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@mentora/ui-components'
import { getTranslations } from 'next-intl/server'

const PCT = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 1,
})

export interface RevenueShareRow {
  id: string
  title: string
  revenueCents: number
}

export async function RevenueShareBars({ rows, formatPrice }: { rows: RevenueShareRow[], formatPrice: PriceFormatter }) {
  const t = await getTranslations('dashboard.revenueShare')

  const totalCents = rows.reduce((acc, r) => acc + r.revenueCents, 0)

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('title')}</CardTitle>
        <CardDescription>{t('description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {totalCents === 0
          ? (
              <p className="py-6 text-center text-sm text-muted-foreground">{t('empty')}</p>
            )
          : (
              rows
                .filter(r => r.revenueCents > 0)
                .map((row) => {
                  const share = row.revenueCents / totalCents
                  return (
                    <div key={row.id} className="space-y-1.5">
                      <div className="flex items-baseline justify-between gap-3 text-sm">
                        <span className="truncate text-foreground">{row.title}</span>
                        <span className="shrink-0 font-mono tabular-nums text-foreground">
                          {formatPrice(row.revenueCents)}
                          <span className="ml-2 text-muted-foreground">
                            (
                            {PCT.format(share)}
                            )
                          </span>
                        </span>
                      </div>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-[var(--chart-1)]"
                          style={{ width: `${Math.max(2, share * 100)}%` }}
                        />
                      </div>
                    </div>
                  )
                })
            )}

        <div className="flex items-center justify-between border-t border-border/60 pt-3 text-sm">
          <span className="font-medium text-foreground">{t('total')}</span>
          <span className="font-mono font-medium tabular-nums text-foreground">
            {formatPrice(totalCents)}
          </span>
        </div>
      </CardContent>
    </Card>
  )
}
